/**
 * Where a scene is drawn, and the one control that stops it.
 *
 * The overlay is a portal on `document.body`, so no page layout changes and
 * no component's `overflow` clips a scene. It takes **no pointer events**:
 * every click, tap, and drag passes straight through to the real page
 * underneath. The Stop button is the single exception, and it is the only
 * part of this that a pointer can reach.
 *
 * The scene itself is `aria-hidden`. A screen reader hears one short
 * announcement when it begins and nothing after that. Focus is never moved:
 * somebody typing in a field keeps their cursor, and the Stop button waits
 * to be tabbed to rather than stealing the caret.
 *
 * The z-index sits above ordinary page content and below a real dialog, so a
 * decorative scene can never cover something that matters.
 */

import { useEffect, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";

export function OverlayHost({
  announce,
  onStop,
  children,
}: {
  readonly announce: string;
  readonly onStop: () => void;
  readonly children: ReactNode;
}) {
  const [host] = useState(() =>
    typeof document === "undefined" ? null : document.createElement("div"),
  );

  useEffect(() => {
    if (host === null) return;
    host.setAttribute("data-arc-effect-overlay", "");
    document.body.append(host);
    return () => host.remove();
  }, [host]);

  if (host === null) return null;

  return createPortal(
    <>
      {/*
        The scene. Decorative throughout: hidden from assistive technology
        and unreachable by a pointer, so nothing here can intercept a click
        meant for the page or read out as content.
      */}
      <div
        className="arc-effect-overlay pointer-events-none fixed inset-0"
        aria-hidden="true"
        data-testid="effect-overlay"
      >
        {children}
      </div>

      {/*
        Said once, when the scene begins. `polite` waits for a gap rather
        than interrupting, and nothing is announced again while it runs.
      */}
      <p className="sr-only" role="status" aria-live="polite">
        {announce}
      </p>

      {/*
        The way out, for somebody who cannot press Escape comfortably or is
        using a touch screen. It sits inside the safe area, is a real button
        with a real name, and does not take focus by itself.
      */}
      <button
        type="button"
        onClick={onStop}
        data-testid="effect-stop"
        className="arc-effect-stop fixed rounded-arc border border-divider bg-paper px-3 py-2 text-sm shadow-lg"
      >
        Stop animation
      </button>
    </>,
    host,
  );
}
